import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';
import brand from '../lib/brand';
import { formatAgo, isFresh } from '../lib/locationAge';
import { Card, CardContent } from './ui/card';

const UB_CENTER = [47.9184, 106.9177];
const REFRESH_MS = 30000;
const STALE_COLOR = '#9ca3af';

// Анх байршилтай жолооч ирэхэд л бүгдийг багтаана; дараагийн шинэчлэлтээр admin-ий зургийг үсрүүлэхгүй
function FitOnce({ points }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || points.length === 0) return;
    done.current = true;
    if (points.length === 1) map.setView(points[0], 14);
    else map.fitBounds(points, { padding: [40, 40] });
  }, [map, points]);
  return null;
}

const Locations = () => {
  const [drivers, setDrivers] = useState([]);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    try {
      const res = await api.get('/drivers/locations');
      setDrivers(res.data);
      setError(null);
    } catch {
      setError('Байршил ачаалахад алдаа гарлаа');
    } finally {
      setNow(Date.now());
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, REFRESH_MS);
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  const located = drivers.filter(d => d.lastLocation);
  const missing = drivers.filter(d => !d.lastLocation);
  const points = located.map(d => [d.lastLocation.lat, d.lastLocation.lng]);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Жолоочийн байршил</h1>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
        <div className="isolate h-[70vh] overflow-hidden rounded-lg border">
          <MapContainer center={UB_CENTER} zoom={12} className="h-full w-full">
            <TileLayer
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <FitOnce points={points} />
            {located.map(d => (
              <CircleMarker
                key={d._id}
                center={[d.lastLocation.lat, d.lastLocation.lng]}
                radius={9}
                pathOptions={{
                  color: isFresh(d.lastLocation.recordedAt, now) ? brand.colors.primary : STALE_COLOR,
                  fillOpacity: 0.85,
                }}
              >
                <Popup>
                  <div className="space-y-0.5 text-sm">
                    <div className="font-semibold">{d.name}</div>
                    <div>{d.phone}{d.vehicleNumber ? ` · ${d.vehicleNumber}` : ''}</div>
                    <div>{formatAgo(d.lastLocation.recordedAt, now)}</div>
                    <div>Идэвхтэй хүргэлт: {d.activeDeliveries}</div>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
        <Card>
          <CardContent className="space-y-3 p-4 text-sm">
            <div className="font-medium">Идэвхтэй хүргэлттэй ({drivers.length})</div>
            {drivers.length === 0 && <p className="text-zinc-500">Одоогоор идэвхтэй хүргэлттэй жолооч алга.</p>}
            {located.map(d => (
              <div key={d._id} className="flex items-center justify-between gap-2">
                <span>{d.name}</span>
                <span className={isFresh(d.lastLocation.recordedAt, now) ? 'text-primary' : 'text-zinc-400'}>
                  {formatAgo(d.lastLocation.recordedAt, now)}
                </span>
              </div>
            ))}
            {missing.length > 0 && (
              <div className="space-y-1 border-t pt-3">
                <div className="text-zinc-500">Байршил ирээгүй</div>
                {missing.map(d => (
                  <div key={d._id} className="flex justify-between">
                    <span>{d.name}</span>
                    <span className="text-zinc-400">{d.activeDeliveries} хүргэлт</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Locations;
